import type { components } from '@/generated/internal-api/openapi-types';
import { ApiService } from '@/support/ApiService';

export type BenchlingLinkStatus = components['schemas']['BenchlingAuthStatusV1.schema']['status'];
export type BenchlingLinkedUser = components['schemas']['BenchlingLinkedUserV1.schema'];

/**
 * Starts the consent flow and returns the Benchling authorize URL to navigate to.
 *
 * `withCredentials` is required, not optional: the response sets a short-lived user-agent binding
 * cookie that the OAuth callback verifies, and this request is cross-origin — the SPA is served from
 * the bare tenant domain while the API answers on the `-api` host. Without it the browser discards the
 * cookie and every callback is rejected as an unbound user agent.
 */
export const startBenchlingAuth = async (): Promise<string> => {
  const response = await ApiService.call({
    endpoint: 'POST /api/v1/benchling-auth/start',
    options: {
      requestConfigOverrides: { withCredentials: true },
      onError: { throw: true, capture: false, toast: false, slug: 'benchling-auth-start' },
    },
  });

  if (response.type !== 'success') {
    throw new Error('Failed to start Benchling delegated auth');
  }
  return response.body.authorize_url;
};

export const fetchBenchlingAuthStatus = async (): Promise<BenchlingLinkStatus> => {
  const response = await ApiService.call({
    endpoint: 'GET /api/v1/benchling-auth/status',
    options: { onError: { throw: true, capture: false, toast: false, slug: 'benchling-auth-status' } },
  });

  if (response.type !== 'success') {
    throw new Error('Failed to load Benchling link status');
  }
  return response.body.status;
};

export const disconnectBenchlingAuth = async (): Promise<void> => {
  const response = await ApiService.call({
    endpoint: 'DELETE /api/v1/benchling-auth/link',
    options: {
      onError: { throw: true, capture: false, toast: false, slug: 'benchling-auth-disconnect' },
    },
  });

  if (response.type !== 'success') {
    throw new Error('Failed to disconnect Benchling account');
  }
};

export const fetchBenchlingLinkedUser = async (): Promise<BenchlingLinkedUser | null> => {
  const response = await ApiService.call({
    endpoint: 'GET /api/v1/benchling-auth/linked-user',
    options: {
      onError: { throw: true, capture: false, toast: false, slug: 'benchling-auth-linked-user' },
    },
  });

  if (response.type !== 'success') {
    throw new Error('Failed to load the linked Benchling account');
  }
  // 204 carries no body: we hold no identity for this link.
  return response.status === 204 ? null : response.body;
};
